import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip
} from 'chart.js';
import { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';

import '../css/Restaurants.css';
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Restaurants() {
  useIdleLogout();
  const [restaurants, setRestaurants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterCuisine, setFilterCuisine] = useState('All');

  useEffect(() => {
    setRestaurants([
      { id: 1, logo: '/assets/img/restaurant.png', name: 'Druk Kitchen', cuisine: 'Bhutanese', location: 'Thimphu', email: 'contact@druk.kitchen', phone: '+975 17 111 222', rating: 4.6, ordersToday: 128, status: 'Open' },
      { id: 2, logo: '/assets/img/restaurant.png', name: 'Himalayan Bites', cuisine: 'Indian', location: 'Thimphu', email: 'hi@himalayanbites.bt', phone: '+975 17 333 444', rating: 4.2, ordersToday: 76, status: 'Open' },
      { id: 3, logo: '/assets/img/restaurant.png', name: 'Dragon Dumplings', cuisine: 'Chinese', location: 'Paro', email: 'hello@dragondumplings.bt', phone: '+975 17 555 666', rating: 3.9, ordersToday: 52, status: 'Closed' },
      { id: 4, logo: '/assets/img/restaurant.png', name: 'QuickBite', cuisine: 'Fast Food', location: 'Thimphu', email: 'support@quickbite.bt', phone: '+975 17 777 888', rating: 4.0, ordersToday: 91, status: 'Suspended' },
      { id: 5, logo: '/assets/img/restaurant.png', name: 'Bean & Leaf', cuisine: 'Cafe', location: 'Punakha', email: 'care@beanleaf.bt', phone: '+975 17 999 000', rating: 4.8, ordersToday: 143, status: 'Open' }
    ]);
  }, []);

  const total = restaurants.length;
  const openCount = restaurants.filter(r => r.status === 'Open').length;
  const closedCount = restaurants.filter(r => r.status === 'Closed').length;
  const suspendedCount = restaurants.filter(r => r.status === 'Suspended').length;

  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return restaurants.filter(r =>
      (filterStatus === 'All' || r.status === filterStatus) &&
      (filterCuisine === 'All' || r.cuisine === filterCuisine) &&
      (
        term === '' ||
        r.name.toLowerCase().includes(term) ||
        r.cuisine.toLowerCase().includes(term) ||
        r.location.toLowerCase().includes(term) ||
        r.email.toLowerCase().includes(term) ||
        r.phone.toLowerCase().includes(term)
      )
    );
  }, [restaurants, searchTerm, filterStatus, filterCuisine]);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: { padding: { bottom: 40 } },
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 12, padding: 8, font: { size: 11 } }
      }
    }
  };

  const statusData = {
    labels: ['Open', 'Closed', 'Suspended'],
    datasets: [{
      data: [openCount, closedCount, suspendedCount],
      backgroundColor: ['#198754', '#6c757d', '#ffc107']
    }]
  };

  const cuisines = useMemo(
    () => Array.from(new Set(restaurants.map(r => r.cuisine))),
    [restaurants]
  );

  const cuisineData = {
    labels: cuisines,
    datasets: [{
      data: cuisines.map(c => restaurants.filter(r => r.cuisine === c).length),
      backgroundColor: ['#0d6efd', '#6f42c1', '#dc3545', '#20c997', '#fd7e14', '#0dcaf0', '#6610f2', '#6c757d']
        .slice(0, cuisines.length)
    }]
  };

  return (
    <>
      {/* Metrics */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 restaurants-metrics">
        {[
          { label: 'Total Restaurants', value: total },
          { label: 'Open', value: openCount },
          { label: 'Closed', value: closedCount },
          { label: 'Suspended', value: suspendedCount }
        ].map((m, i) => (
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Restaurant Status Distribution</div>
            <div className="card-body">
              <Doughnut data={statusData} options={doughnutOptions} />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Cuisine Type Breakdown</div>
            <div className="card-body">
              <Doughnut data={cuisineData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters — inline row */}
      <div className="card mb-3 search-filter-card">
        <div className="card-body searchbar-row-inline">
          <span className="search-icon">
            <i className="bi bi-search" />
          </span>

          <div className="input-group search-pill flex-grow-1">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, cuisine, location, email, phone..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="ghost-select">
            <select
              className="form-select select-ghost"
              value={filterCuisine}
              onChange={e => setFilterCuisine(e.target.value)}
            >
              <option value="All">All Cuisines</option>
              {cuisines.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="ghost-select">
            <select
              className="form-select select-ghost"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
              <option value="Suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="restaurants-table-card">
        <div className="table-responsive">
          <table className="table table-hover align-middle table-pro">
            <colgroup>
              <col style={{ width: '70px' }} />
              <col style={{ width: '64px' }} />
              <col style={{ width: '160px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '220px' }} />  {/* Email */}
              <col style={{ width: '160px' }} /> {/* Phone */}
              <col style={{ width: '110px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '180px' }} />
            </colgroup>

            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Image</th>
                <th>Name</th>
                <th>Cuisine</th>
                <th>Location</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Rating</th>
                <th>Orders Today</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredList.map((r, i) => (
                <tr key={r.id}>
                  <td>{i + 1}</td>
                  <td>
                    <img
                      src={r.logo}
                      alt=""
                      className="rounded-circle object-cover"
                      width="36"
                      height="36"
                    />
                  </td>
                  <td className="name-cell">{r.name}</td>
                  <td>{r.cuisine}</td>
                  <td>{r.location}</td>
                  <td className="email-cell" title={r.email}>{r.email}</td>
                  <td className="phone-cell">{r.phone}</td>
                  <td className="rating-cell">
                    <span className="rating-stars">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <i
                          key={idx}
                          className={`bi ${r.rating >= idx + 1
                              ? 'bi-star-fill'
                              : r.rating > idx
                                ? 'bi-star-half'
                                : 'bi-star'
                            }`}
                        />
                      ))}
                    </span>
                    <span className="rating-number">({r.rating.toFixed(1)})</span>
                  </td>
                  <td>{r.ordersToday}</td>
                  <td>
                    <span className={`badge status-${r.status.toLowerCase()}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon view" aria-label="View"><i className="bi bi-eye" /></button>
                      <button className="btn-icon edit" aria-label="Edit"><i className="bi bi-pencil-square" /></button>
                      <button className="btn-icon delete" aria-label="Delete"><i className="bi bi-trash" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredList.length === 0 && (
                <tr>
                  <td colSpan="11" className="text-center py-3 text-muted">
                    No restaurants match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} SuperApp Admin • All rights reserved</small>
      </footer>
    </>
  );
}
